import React, { useState, useEffect } from "react";
import Icon from "../../components/ui/icon";
import "../../assets/styles/pages/publishers/campaignLinkTest.css";
import SideNav from "../../components/layout/sideNav";
import { useToast } from "../../context/toastContext";
import { useNavigate } from "react-router-dom";
import api from "../../services/apiClient";
import DOMPurify from "dompurify";

const CampaignLinkTestPage = () => {
    const navigate = useNavigate();
    const showToast = useToast();
    const [isSideNavActive, setisSideNavActive] = useState(false);
    const [userInfo, setUserInfo] = useState(JSON.parse(localStorage.getItem("PublisherInfo")) || {});
    const [campaignLink, setCampaignLink] = useState("");
    const [testRunning, setTestRunning] = useState(false);
    const [testResults, settestResults] = useState(null);
    const [expandedStep, setExpandedStep] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem("aToken");
        if (!token) {
            navigate("/auth/login");
            return;
        }

        api.get(`/api/publisher/info`)
            .then((response) => {
                const data = response.data.data;
                setUserInfo(data);
                localStorage.setItem("PublisherInfo", JSON.stringify(data));
            })
            .catch((err) => {
                showToast({ status: "error", message: "Session expired. Please login again." });
                navigate("/auth/login");
            });
    }, [navigate, showToast]);

    const handleRunTest = async () => {
        if (!campaignLink.trim()) {
            return showToast({
                status: "error",
                message: "Please enter your tracking link.",
            });
        }

        const cleanLink = DOMPurify.sanitize(campaignLink);
        const urlRegex = /^(https?:\/\/)[^\s/$.?#].[^\s]*$/;

        if (!urlRegex.test(cleanLink)) {
            return showToast({
                status: "error",
                message: "Invalid tracking link. Please enter a valid URL.",
            });
        }

        try {
            new URL(cleanLink);
        } catch {
            return showToast({
                status: "error",
                message: "Invalid URL format.",
            });
        }

        setTestRunning(true);

        try {
            const res = await api.post("/api/campaign/test-link", {
                campaignLink: cleanLink,
            });

            settestResults(res.data);

            if (res?.data?.status) {
                showToast({
                    status: "success",
                    message: "Link test completed successfully.",
                });
            } else {
                showToast({
                    status: "error",
                    message: res?.data?.message || "Link test failed.",
                });
            }
        } catch (error) {
            console.error("Link test error:", error);

            showToast({
                status: "error",
                message:
                    error?.response?.data?.message ||
                    "Unable to run link test. Please try again.",
            });
        } finally {
            setTestRunning(false);
        }
    };

    return (
        <div className={`campaign_link_test__page`}>

            <SideNav
                userName={userInfo.name}
                emailID={userInfo.email}
                active={isSideNavActive}
                campaignOpenInital={false}
                accManagerName={userInfo.adminInfo?.name}
                accManagerEmail={userInfo.adminInfo?.email}
            />

            <div
                onClick={() => setisSideNavActive(false)}
                className={`background-shadow ${isSideNavActive ? "active" : ""}`}
            ></div>

            <main>
                <header className="desktop-header">
                    <div>
                        <h2>Integration Tester</h2>
                        <p>Test your campaign links and verify postback integration</p>
                    </div>
                </header>

                <header className="mobile-header">
                    <div>
                        <h2>Integration Tester</h2>
                    </div>
                    <button
                        className="nav-btn"
                        onClick={() => setisSideNavActive(!isSideNavActive)}
                    >
                        {isSideNavActive ? (
                            <Icon name="XMark" className="onLeft" />
                        ) : (
                            <Icon name="Bars3" className="onLeft" />
                        )}
                    </button>
                </header>

                <div className="main-container">
                    <section className="card test-input-card">
                        <div className="card-header">
                            <div>
                                <h3>Test Campaign Link</h3>
                                <p>Enter your tracking link to verify flow</p>
                            </div>
                            <span className="badge">Ready</span>
                        </div>

                        <div className="input-group">
                            <div className="input-with-icon">
                                <Icon name="Link" className="input-icon" />
                                <input
                                    type="url"
                                    placeholder="https://your-link.com?aff_click_id={click_id}"
                                    value={campaignLink}
                                    onChange={(e) => setCampaignLink(e.target.value)}
                                    disabled={testRunning}
                                />
                            </div>

                            <div className="test-actions">
                                <button className="primary" onClick={handleRunTest} disabled={testRunning}>
                                    <Icon name={testRunning ? "ArrowPath" : "Play"} className={testRunning ? "spin onLeft" : "onLeft"} />
                                    {testRunning ? "Running Test..." : "Run Test"}
                                </button>
                            </div>
                        </div>
                    </section>

                    {testResults && <section className="card results-card">
                        <div className="card-header">
                            <div>
                                <h3>Test Result</h3>
                                <p>{testResults.message}</p>
                            </div>
                            <span className={`status-pill ${testResults.status ? 'success' : 'failed'}`}>
                                {testResults.status ? 'Passed' : 'Failed'}
                            </span>
                        </div>

                        <div className="steps-container">
                            {testResults.identifiedInfo.steps.map((step, index) => (
                                <div className="step-item" key={index}>
                                    <div className="step-line-container">
                                        <div className={`step-dot ${step.status}`}>
                                            {step.status === 'completed' ? <Icon name="Check" size={14} /> : <Icon name="X" size={14} />}
                                        </div>
                                        {index !== testResults.identifiedInfo.steps.length - 1 && <div className="step-connector"></div>}
                                    </div>

                                    <div className="step-content">
                                        <div className="step-main" onClick={() => setExpandedStep(expandedStep === index ? null : index)}>
                                            <div className="step-info">
                                                <h4>{step.name}</h4>
                                                <p>{step.description}</p>
                                            </div>
                                            <Icon
                                                name="ChevronDown"
                                                className={`detail-toggle ${expandedStep === index ? 'rotate' : ''}`}
                                            />
                                        </div>

                                        {expandedStep === index && (
                                            <div className="step-details-box">
                                                <pre>{JSON.stringify(step.details, null, 2)}</pre>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>}

                    <section className="card help-card">
                        <div className="card-header">
                            <h3>How It Works</h3>
                        </div>
                        <div className="help-steps">
                            {[
                                { t: "Enter Link", d: "Paste your tracking link with placeholders." },
                                { t: "Simulate Click", d: "We generate a unique click ID for this test." },
                                { t: "Verify Postback", d: "We ping your URL to ensure data is received." }
                            ].map((step, i) => (
                                <div className="help-step" key={i}>
                                    <div className="step-number">{i + 1}</div>
                                    <div>
                                        <h4>{step.t}</h4>
                                        <p>{step.d}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
            </main>
        </div>
    );
};

export default CampaignLinkTestPage;